import { LitElement, css, html } from 'lit';
import { customElement } from 'lit/decorators.js';
import { getGlobalStyleSheets } from '../../global-styles';

import * as TagNames from './nav-tabs.tagnames';
import { AtlNavTabPane } from './nav-tab-pane.component';
import { ifDefined } from 'lit/directives/if-defined.js';
import { classMap } from 'lit/directives/class-map.js';
import { AtlNavTabClickEvent } from './nav-tabs.events';

@customElement(TagNames.NavTabs)
export class AtlNavTabs extends LitElement {
   static styles = [
      ...getGlobalStyleSheets(),
      css`
         :host {
            display: block;
         }
      `,
   ];
   static properties = {
      activeTabName: { type: String, state: true },
   };

   declare activeTabName?: string;

   constructor() {
      super();
      this.addEventListener(AtlNavTabClickEvent.EVENT_NAME, (e: Event) => {
         this.activeTabName = (e as AtlNavTabClickEvent).detail.tabName;
      });
   }

   private get _navTabPanes() {
      const slot = this.shadowRoot?.querySelector('slot');
      const assignedNodes = slot?.assignedNodes({ flatten: true });

      const navTabPanes: AtlNavTabPane[] = [];
      assignedNodes?.forEach((node) => {
         if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as HTMLElement;
            if (element.tagName === TagNames.NavTabPane.toUpperCase()) {
               navTabPanes.push(element as AtlNavTabPane);
            }
         }
      });
      return navTabPanes;
   }

   private _onSlotChange = () => {
      this._navTabPanes.forEach((tabPane) => {
         if (tabPane.active) {
            this.activeTabName = tabPane.tabName;
         }
      });

      this.requestUpdate();
   };

   private _renderTabs = () => {
      return this._navTabPanes.map((tabPane, idx) => {
         const classNames = {
            'nav-link': true,
            active: tabPane.tabName === this.activeTabName,
         };
         return html`<li
            class="nav-item"
            aria-label=${ifDefined(tabPane.tabName)}
         >
            <button
               type="button"
               class=${classMap(classNames)}
               tabindex="0"
               part="nav-tab-button"
               @click=${() =>
                  this.dispatchEvent(
                     new AtlNavTabClickEvent(idx, tabPane.tabName)
                  )}
            >
               ${tabPane.tabLabel}
            </button>
         </li>`;
      });
   };

   render() {
      return html`
         <div class="tabs-container" part="nav-tabs-container">
            <ul class="flex-nowrap nav nav-tabs tabs-mainheader" part="nav-tabs-bar">
               ${this._renderTabs()}
            </ul>

            <div class="tab-content" part="nav-tabs-content">
               <slot @slotchange=${this._onSlotChange}></slot>
            </div>
         </div>
      `;
   }
}
